import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, MonitorIcon as Monitor, ArrowsClockwiseIcon as ArrowsClockwise, CircleNotchIcon as Spinner, ListNumbersIcon as ListNumbers, PlusIcon as Plus } from "@phosphor-icons/react";
import { createScreenBinding, deactivateScreenBinding, getWorkspace, listScreenBindings, listWorkspaceMembers, rebuildScreenBindings, updateScreenBinding } from "@/lib/api";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import BindingSummaryStrip from "@/components/screenbindings/BindingSummaryStrip";
import BindingFilters from "@/components/screenbindings/BindingFilters";
import BindingTable from "@/components/screenbindings/BindingTable";
import BindingDetail from "@/components/screenbindings/BindingDetail";
import ManualBindingForm from "@/components/screenbindings/ManualBindingForm";

const PAGE = 100;
const FILTER_KEYS = ["q", "program_name", "display_file", "record_format", "operation", "status", "confidence", "origin", "include_inactive"];

/** Iter 45 / 48 — observed screen format bindings (RPG EXFMT / WRITE / READ / READC → parsed DSPF format) + manual bindings. */
export default function WorkspaceScreenBindings() {
  const { id: workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [selectedId, setSelectedId] = useState(() => Number(params.get("binding")) || null);
  const [form, setForm] = useState(null);          // null | {} (create) | binding (edit)
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const offset = Number(params.get("offset")) || 0;
  const canWrite = user?.role === "admin" || myRole === "owner" || myRole === "editor";

  useEffect(() => {
    getWorkspace(workspaceId).then((d) => setWorkspace(d?.workspace || null)).catch(() => {});
    listWorkspaceMembers(workspaceId)
      .then((m) => setMyRole((m.members || []).find((x) => x.user_id === user?.id)?.role || null))
      .catch(() => setMyRole(null));
  }, [workspaceId, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = { limit: PAGE, offset };
      for (const k of FILTER_KEYS) if (filters[k]) query[k] = filters[k];
      setPage(await listScreenBindings(workspaceId, query));
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not load screen format bindings");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, offset, filters]);
  useEffect(() => { load(); }, [load]);

  const updateParams = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === "" || v === null || v === undefined) next.delete(k); else next.set(k, String(v));
    }
    setParams(next, { replace: true });
  };
  const setFilters = (f) => updateParams({ ...f, offset: "" });
  const clearFilters = () => updateParams(Object.fromEntries([...FILTER_KEYS, "offset"].map((k) => [k, ""])));
  const openBinding = (id) => { setSelectedId(id); updateParams({ binding: id }); };
  const closeBinding = () => { setSelectedId(null); updateParams({ binding: "" }); };
  const refreshAll = () => { load(); setRefreshKey((k) => k + 1); };

  const handleRebuild = async () => {
    if (!window.confirm("Rebuild screen format bindings from parsed RPG and DSPF source? Parser bindings are upserted (no duplicates); vanished ones are removed; field references and the walkthrough follow. Manual bindings are never touched.")) return;
    setRebuilding(true);
    try {
      const r = await rebuildScreenBindings(workspaceId);
      toast.success(`Bindings: ${r.inserted} new, ${r.updated} updated, ${r.deleted} removed · ${r.bound} bound (${r.high} high), ${r.ambiguous} ambiguous, ${r.unresolved} unresolved · ${r.manual_active} manual kept · field references ${r.field_references.total} total.`);
      refreshAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const submitForm = async (payload) => {
    setFormBusy(true);
    setFormError(null);
    try {
      const editing = Boolean(form?.id);
      const r = editing ? await updateScreenBinding(workspaceId, form.id, payload) : await createScreenBinding(workspaceId, payload);
      const b = r.binding;
      toast.success(`${editing ? "Updated" : "Added"} manual binding #${b.id}: ${b.program_name} ${b.operation.toUpperCase()} ${b.display_file}/${b.record_format} — not parser-proven. Field references ${r.field_references.total} total · walkthrough ${r.screen_walkthrough.manual} manual step${r.screen_walkthrough.manual === 1 ? "" : "s"}.`);
      for (const w of r.warnings || []) toast.warning(w, { duration: 8000 });
      for (const n of r.resolves || []) toast.message(n, { duration: 6000 });
      setForm(null);
      openBinding(b.id);
      refreshAll();
    } catch (e) {
      setFormError(formatApiErrorDetail(e) || "Could not save the manual binding");
    } finally {
      setFormBusy(false);
    }
  };

  const handleDeactivate = async (b) => {
    if (!window.confirm(`Deactivate manual binding #${b.id} (${b.program_name} ${b.operation.toUpperCase()} ${b.display_file}/${b.record_format})? It stops feeding field references and the walkthrough; the row stays for the audit trail and can be re-added.`)) return;
    try {
      await deactivateScreenBinding(workspaceId, b.id);
      toast.success(`Manual binding #${b.id} deactivated.`);
      refreshAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not deactivate");
    }
  };

  return (
    <div className="space-y-6" data-testid="screen-bindings-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to={`/workspaces/${workspaceId}/metadata/field-dependencies`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1" data-testid="sb-back-to-field-deps">
            <ArrowLeft size={11} weight="bold" /> Back to field dependencies
          </Link>
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <Monitor size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Screen format bindings
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-3xl">
            Which RPG program shows a screen format — the program statement that displays or reads the format (EXFMT / WRITE / READ / READC),
            matched to the parsed display file that defines it. Bound only when exactly one parsed DSPF has the format; the same format in
            several files is an ambiguous format name; a format no parsed DSPF defines is an unresolved format usage. Where the parser cannot
            prove the relationship, a consultant can add a <strong className="text-[var(--ml-text-primary)]">manual binding</strong> — marked Manual,
            never parser-proven, kept across rebuilds, and used for walkthrough and field reference assignment. No LLM inference.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
            Workspace<br /><span className="text-[var(--ml-text-primary)]">{workspace?.name || "—"}</span>
          </div>
          {canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={() => { setForm({}); setFormError(null); }} className="ml-btn-primary flex items-center gap-1.5" data-testid="sb-add-manual-btn">
                <Plus size={14} weight="bold" /> Add manual binding
              </button>
              <button type="button" onClick={handleRebuild} disabled={rebuilding} className="ml-btn-secondary flex items-center gap-1.5" data-testid="sb-rebuild-btn"
                title="Recompute from parsed RPG + DSPF source. Idempotent; manual bindings untouched.">
                {rebuilding ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
                {rebuilding ? "Rebuilding…" : "Rebuild bindings"}
              </button>
            </div>
          )}
          <Link to={`/workspaces/${workspaceId}/metadata/screen-walkthrough`} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sb-walkthrough-link">
            <ListNumbers size={11} weight="bold" /> Screen walkthrough →
          </Link>
        </div>
      </div>

      {form !== null && (
        <ManualBindingForm
          initial={form.id ? form : null}
          onSubmit={submitForm}
          onCancel={() => { setForm(null); setFormError(null); }}
          busy={formBusy}
          serverError={formError}
        />
      )}

      <BindingSummaryStrip summary={page?.summary} onFilter={setFilters} />
      <BindingFilters filters={filters} onChange={setFilters} onClear={clearFilters} />

      <div className={`grid grid-cols-1 gap-4 ${selectedId ? "lg:grid-cols-3" : ""}`}>
        <div className={selectedId ? "lg:col-span-2" : ""}>
          <BindingTable page={page} loading={loading} selectedId={selectedId} onSelect={openBinding} onPage={(o) => updateParams({ offset: o || "" })} />
        </div>
        {selectedId && (
          <BindingDetail
            workspaceId={workspaceId}
            bindingId={selectedId}
            onClose={closeBinding}
            canWrite={canWrite}
            refreshKey={refreshKey}
            onEdit={(b) => { setForm({ id: b.id, program_library: b.program_library || "", program_name: b.program_name, display_file: b.display_file || "", record_format: b.record_format, operation: b.operation, confidence: b.confidence, manual_note: b.manual_note || "" }); setFormError(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            onDeactivate={handleDeactivate}
          />
        )}
      </div>
    </div>
  );
}
